import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { IconButton, Menu, MenuItem, MenuList, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import GamesIcon from '@mui/icons-material/Games';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import UsersIcon from '@mui/icons-material/People';
import SwitchIcon from '@mui/icons-material/SwitchAccount';
import UserIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import RankingIcon from '@mui/icons-material/Leaderboard';
import GameIcon from '@mui/icons-material/SportsEsports';

import { useTitle } from '../../../hooks/titleProvider';
import { useAuth } from '../../../contexts/authContext';
import { useColorMode } from '../../../hooks/colorModeProvider';

import { signOutUser } from '../../../services/auth';
import { useAlert } from '../../../contexts/alertContext';

import BowlscoutLogo from '../../../resources/assets/images/bowlscout_logo_text_next.png';
import BowlscoutWhiteLogo from '../../../resources/assets/images/bowlscout_logo_text_next_white.png';
import { Link, useNavigate } from 'react-router-dom';
import { Roles } from '../../../helpers/access';
import VenueSwitcher from '../../venues/switcher';

const drawerWidth = 240;

export default function MainNavigation({ children }) {
  const { title } = useTitle();
  const { authenticated, currentUserProfile } = useAuth();
  const { mode, toggleColorMode } = useColorMode();
  const { showAlertError, showAlertSuccess } = useAlert();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const theme = useTheme();

  const activeMenuItem = window.location.pathname;

  const [navItems, setNavItems] = useState([
    {
      text: 'Overview',
      secondaryText: '',
      link: '/overview',
      icon: <DashboardIcon color="lightIcon" />,
      access: Roles.USER
    },
    {
      text: 'My Games',
      secondaryText: '',
      link: '/my-games',
      icon: <GameIcon color="lightIcon" />,
      access: Roles.USER
    },
    // {
    //   text: 'Logs',
    //   secondaryText: '',
    //   link: '/logs',
    //   icon: <RankingIcon color="lightIcon" />
    // },
    // {
    //   text: 'New Private Game',
    //   secondaryText: '',
    //   link: '/new',
    //   icon: <GamesIcon color="lightIcon" />,
    //   active: false,
    //   access: Roles.USER
    // },
    {
      text: 'Events',
      secondaryText: '',
      link: '/events',
      icon: <EventIcon color="lightIcon" />,
      access: Roles.USER
    }
    // {
    //   text: 'Tournaments',
    //   secondaryText: '',
    //   link: '/tournaments',
    //   icon: <GamesIcon color="lightIcon" />,
    //   access: Roles.USER
    // }
  ]);

  const [userMenuItems, setUserMenuItems] = useState([
    {
      text: 'Profile',
      secondaryText: '',
      link: '/profile',
      icon: <UserIcon color="lightIcon" />,
      access: Roles.USER
    },
    {
      text: 'Settings',
      secondaryText: '',
      link: '/settings',
      icon: <SettingsIcon color="lightIcon" />,
      access: Roles.USER
    },
    {
      text: 'Sign Out',
      secondaryText: '',
      link: '/signout',
      icon: <LogoutIcon color="lightIcon" />,
      access: Roles.USER
    }
  ]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const fullName = currentUserProfile.firstName + ' ' + currentUserProfile.lastName;

  const handleSignOut = async () => {
    await signOutUser()
      .then(() => {
        showAlertSuccess('Successfully signed out!');
      })
      .catch((err) => {
        showAlertError('Error signing out. Please try again.');
      });
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={
          !isMobile
            ? { width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` }
            : { width: '100%' }
        }>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => {
                setUserMenuAnchor(null);
                setMobileMenuOpen(!userMenuOpen);
              }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Box display={'flex'} justifyContent={'flex-end'}>
            <IconButton sx={{ ml: 1 }} onClick={toggleColorMode} color="inherit">
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={(e) => {
                setUserMenuAnchor(e.currentTarget);
                setUserMenuOpen(true);
              }}>
              <UserIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {!isMobile && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box'
            }
          }}
          variant="permanent"
          anchor="left">
          <Toolbar sx={{ display: 'flex', justifyContent: 'center' }}>
            {/* Logo Image */}
            {mode === 'dark' ? (
              <img src={BowlscoutWhiteLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            ) : (
              <img src={BowlscoutLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            )}
          </Toolbar>
          <Divider />
          <MenuList>
            {navItems.map((item, index) => (
              <MenuItem
                key={item.link}
                selected={activeMenuItem === item.link}
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.main'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'primary.main'
                  }
                }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.secondaryText}
                  onClick={() => {
                    if (item.link) {
                      navigate(item.link);
                    }
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'primary.dark'
                    }
                  }}
                />
              </MenuItem>
            ))}
          </MenuList>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          {authenticated && currentUserProfile && (
            <>
              <MenuItem
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
                onClick={(e) => {
                  setUserMenuAnchor(e.currentTarget);
                  setUserMenuOpen(true);
                }}>
                <ListItemIcon>
                  <img
                    src={
                      currentUserProfile.avatarUrl ||
                      'https://ui-avatars.com/api/?name=' + encodeURIComponent(fullName || 'User')
                    }
                    alt="Profile"
                    style={{ width: 32, height: 32, borderRadius: '50%' }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ ml: 1 }}
                  primary={fullName}
                  secondary={currentUserProfile.email}
                />
              </MenuItem>
              <Menu
                anchorEl={userMenuAnchor}
                open={userMenuOpen}
                onClose={() => setUserMenuOpen(false)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}>
                <VenueSwitcher />
                {userMenuItems.map((item, idx) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => {
                      setUserMenuOpen(false);
                      if (item.text === 'Sign Out') {
                        handleSignOut();
                      } else {
                        navigate(item.link);
                      }
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white'
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Drawer>
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <Menu
          anchorEl={userMenuAnchor}
          open={userMenuOpen}
          onClose={() => setUserMenuOpen(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}>
          <VenueSwitcher />
          {userMenuItems.map((item, idx) => (
            <MenuItem
              key={item.text}
              onClick={() => {
                setUserMenuOpen(false);
                if (item.text === 'Sign Out') {
                  handleSignOut();
                } else {
                  navigate(item.link);
                }
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Mobile Main Menu - Drawer from the top */}
      {isMobile && (
        <Drawer
          anchor="top"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              boxSizing: 'border-box',
              pb: 3,
              px: 2
            }
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            {mode === 'dark' ? (
              <img src={BowlscoutWhiteLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            ) : (
              <img src={BowlscoutLogo} alt="Logo" style={{ width: '150px', height: 'auto' }} />
            )}
          </Box>
          <List>
            {navItems.map((item, index) => (
              <ListItem key={item.link} disablePadding>
                <ListItemButton
                  selected={activeMenuItem === item.link}
                  onClick={() => {
                    if (item.link) {
                      navigate(item.link);
                    }
                    setMobileMenuOpen(false);
                  }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}

      {/* Main Content */}

      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
